import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Check,
  Pencil,
  Phone,
  KeyRound,
  RefreshCw,
  MessagesSquare,
  FolderOpen,
  DollarSign,
  CalendarClock,
  Hash,
  Users,
  Ban,
  ShieldCheck,
  ClipboardList,
  Thermometer,
  CalendarPlus,
  ArrowUpRight,
  Trash2,
  Tag as TagIcon,
  Plus,
  StickyNote,
  Loader2,
  UserCheck,
  UserX,
} from 'lucide-react';
import type { ConversationStatus, LeadStatus } from '@conversia/shared-types';
import type { ContactDetails } from '@/lib/types';
import * as contactsApi from '@/api/contacts.api';
import * as conversationsApi from '@/api/conversations.api';
import * as tagsApi from '@/api/tags.api';
import { BotControlToggle } from '@/components/conversation/BotControlToggle';
import { JobFormModal } from '@/components/jobs/JobFormModal';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';
import { formatPhoneNumber, dateTime } from '@/lib/format';
import { relativeTime } from '@/lib/format';
import { getContactDisplayName } from '@/lib/contact-display';
import { useAuthStore } from '@/store/auth.store';
import { debounce } from '@/lib/debounce';
import {
  LEAD_STATUS_OPTIONS,
  leadStatusOptionsFrom,
  editableLeadFieldsFrom,
  INTERNAL_NOTES_KEY,
  manualEditsFrom,
  seccionesVisibles,
  valorDeFicha,
  quickActionTransition,
  type EditableLeadField,
  type QuickAction,
} from '@/lib/lead-fields';
import { useCrmView } from '@/hooks/useCrmView';
import { cambiarEtapa } from '@/api/cartera.api';
import { cn } from '@/lib/cn';
import { nombreLegible } from '@/lib/nombre-legible';
import { confirmarTemperatura } from '@/lib/tablero-clientes';
import { EditorDeCampo } from '@/components/cartera/EditorDeCampo';

/**
 * Panel lateral derecho (slide-in) con la ficha del contacto: identidad editable,
 * número (resuelto o pendiente, con reintento), ID interno, estadísticas y costo
 * API estimado. Se abre desde la cabecera del chat (botón ℹ️) sin tapar la
 * conversación. v0.5.
 *
 * Ficha del lead: además expone, cuando se abre desde una
 * conversación (`conversationId`), la temperatura del lead editable y las acciones
 * rápidas (Agendar/Escalar/Descartar). Los datos detectados editables, las
 * etiquetas y las notas internas operan sobre el contacto. Todo reusa endpoints
 * existentes (conversations/contacts/tags); aditivo, no toca el flujo previo.
 *
 * Tanda 6: el panel es solo el envoltorio (portal, velo, cabecera, pie y Escape). El
 * contenido es `FichaDelContacto`, el mismo que usa la ventana de «Clientes».
 */
export function ContactDetailsPanel({
  contactId,
  conversationId,
  conversationStatus,
  leadStatus,
  open,
  onClose,
}: {
  contactId?: string;
  /** Conversación abierta desde la que se invocó el panel (para temperatura/acciones). */
  conversationId?: string;
  conversationStatus?: ConversationStatus;
  leadStatus?: LeadStatus | null;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  // La misma consulta que hace la ficha (una sola petición): el pie necesita saber si está bloqueado.
  const { data } = useQuery({
    queryKey: ['contact', contactId, 'details'],
    queryFn: () => contactsApi.getContactDetails(contactId as string),
    enabled: open && Boolean(contactId),
  });

  // Cierra el panel con Escape y bloquea el scroll del body mientras está abierto.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const toggleBlock = useMutation({
    mutationFn: (blocked: boolean) =>
      contactsApi.updateContact(contactId as string, { blocked }),
    onSuccess: (_res, blocked) => {
      void qc.invalidateQueries({ queryKey: ['contact', contactId] });
      toast.success(blocked ? 'Contacto bloqueado' : 'Contacto desbloqueado');
    },
    onError: () => toast.error('No se pudo cambiar el bloqueo'),
  });

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Detalles del contacto"
        className="absolute right-0 top-0 flex h-full w-full max-w-sm flex-col border-l border-surface-border bg-white shadow-xl animate-slide-in-right dark:border-slate-800 dark:bg-slate-900"
      >
        {/* Cabecera */}
        <div className="flex items-center justify-between border-b border-surface-border p-4 dark:border-slate-800">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Detalles del contacto</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {conversationId && (
          <div className="border-b border-surface-border p-3 dark:border-slate-800">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                onClose();
                navigate(`/conversations/${conversationId}`);
              }}
            >
              <MessagesSquare className="h-3.5 w-3.5" /> Abrir el chat
            </Button>
          </div>
        )}

        <FichaDelContacto
          contactId={contactId}
          conversationId={conversationId}
          conversationStatus={conversationStatus}
          leadStatus={leadStatus}
          className="flex-1 overflow-y-auto p-5"
        />

        {/* Acciones */}
        {data && (
          <div className="flex items-center gap-2 border-t border-surface-border p-4 dark:border-slate-800">
            <ContactBlockButton
              blocked={data.blocked}
              onToggle={(b) => toggleBlock.mutate(b)}
              loading={toggleBlock.isPending}
            />
            <Button variant="ghost" size="sm" className="ml-auto" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        )}
      </aside>
    </div>,
    document.body,
  );
}

/**
 * LA FICHA DEL CONTACTO, sin envoltorio (tanda 6). Una sola ficha con dos envoltorios: el
 * panel lateral de Chats (`ContactDetailsPanel`) y la ventana de Clientes (`VentanaDeFicha`).
 * Se monta cuando se abre y se desmonta al cerrar: así no arrastra una edición a medias.
 *
 * Va en tres bloques (quién es y cómo va su chat; sus datos; lo demás) que, uno debajo del
 * otro, son la ficha de siempre. Con `dosColumnas`, en escritorio los datos pasan a la derecha.
 */
export function FichaDelContacto({
  contactId,
  conversationId,
  conversationStatus,
  leadStatus,
  dosColumnas = false,
  className,
}: {
  contactId?: string;
  /** La conversación desde la que se abre (para temperatura, historial y acciones rápidas). */
  conversationId?: string;
  conversationStatus?: ConversationStatus;
  leadStatus?: LeadStatus | null;
  dosColumnas?: boolean;
  /** Del bloque con el contenido (relleno, scroll…); los estados de carga y de error no lo usan. */
  className?: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const secciones = seccionesVisibles(useCrmView()?.crm);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['contact', contactId, 'details'],
    queryFn: () => contactsApi.getContactDetails(contactId as string),
    enabled: Boolean(contactId),
  });

  // Motivo de la temperatura + historial de cambios (solo con conversación).
  const { data: conversacion } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => conversationsApi.getConversation(conversationId as string),
    enabled: Boolean(conversationId),
  });
  const { data: temperaturas } = useQuery({
    queryKey: ['conversation', conversationId, 'temperaturas'],
    queryFn: () => conversationsApi.listTemperaturas(conversationId as string),
    enabled: Boolean(conversationId),
  });

  // Reinicia el modo edición al cambiar de contacto.
  useEffect(() => {
    setEditingName(false);
    setEditingPhone(false);
  }, [contactId]);

  const saveName = useMutation({
    mutationFn: (name: string) =>
      contactsApi.updateContact(contactId as string, { name: name.trim() || null }),
    onSuccess: () => {
      setEditingName(false);
      void qc.invalidateQueries({ queryKey: ['contact', contactId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['conversation'] });
      void qc.invalidateQueries({ queryKey: ['cartera'] });
      toast.success('Nombre actualizado');
    },
    onError: () => toast.error('No se pudo actualizar el nombre'),
  });

  // Corrección manual del teléfono (tanda edicion-completa). No toca el ruteo
  // de mensajes: los envíos van por externalId/JID; phone es display y wa.me.
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState('');
  const savePhone = useMutation({
    mutationFn: (phone: string) => contactsApi.updateContact(contactId as string, { phone }),
    onSuccess: () => {
      setEditingPhone(false);
      void qc.invalidateQueries({ queryKey: ['contact', contactId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['cartera'] });
      toast.success('Teléfono actualizado');
    },
    onError: () => toast.error('No se pudo actualizar el teléfono'),
  });
  function commitPhone() {
    const digits = phoneDraft.replace(/[^\d+]/g, '');
    if (digits.replace(/\D/g, '').length < 6) {
      toast.error('Teléfono demasiado corto');
      return;
    }
    savePhone.mutate(digits);
  }

  // «Es personal» (tanda 5, Ola E): familia y amigos del dueño. El agente no les escribe.
  const togglePersonal = useMutation({
    mutationFn: (personal: boolean) => contactsApi.marcarPersonal(contactId as string, personal),
    onSuccess: (_res, personal) => {
      void qc.invalidateQueries({ queryKey: ['contact', contactId] });
      void qc.invalidateQueries({ queryKey: ['cartera'] });
      void qc.invalidateQueries({ queryKey: ['agents'] });
      if (personal) toast.success('Listo: el agente ya no le escribe.', 'Tampoco le prepara seguimientos.');
      else toast.success('Ya no es personal.', 'El agente puede volver a atenderlo.');
    },
    onError: () => toast.error('No se pudo cambiar si es personal'),
  });

  const retryResolve = async () => {
    const res = await refetch();
    if (res.data?.phone) toast.success('Número resuelto');
    else toast.info('WhatsApp no expuso el número todavía');
  };

  if (isError) {
    // Error explícito con reintento: nunca un spinner infinito.
    return (
      <div className="flex min-h-[10rem] flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-slate-500 dark:text-slate-400">No se pudo cargar la ficha.</p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Reintentar
        </Button>
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="flex min-h-[10rem] flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // Sin nombre, el contacto se llama como su teléfono: con formato, igual que en la tarjeta de «Clientes».
  const displayName = nombreLegible(getContactDisplayName(data));
  const showPushName = data.pushName && data.pushName.trim() !== (data.name ?? '').trim();
  // Fechas de edición manual por campo (sella el server): chips "a mano".
  const manual = manualEditsFrom(data.leadCard);

  return (
    <div
      className={cn(
        'space-y-5',
        dosColumnas && 'md:grid md:grid-cols-2 md:items-start md:gap-x-8 md:gap-y-5 md:space-y-0',
        className,
      )}
    >
      {/* Quién es y cómo va su chat */}
      <div className={cn('space-y-5', dosColumnas && 'md:col-start-1')}>
        {/* Identidad */}
        <div className="flex flex-col items-center text-center">
          <Avatar
            name={displayName}
            src={data.avatarUrl}
            isGroup={data.isGroup}
            seed={data.externalId}
            size="lg"
          />
          <div className="mt-3 w-full">
            {editingName ? (
              <div className="flex items-center gap-1.5">
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveName.mutate(nameDraft);
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                  onBlur={() => saveName.mutate(nameDraft)}
                  className="h-9 w-full rounded-md border border-brand-400 bg-white px-2 text-center text-sm font-semibold focus:outline-none dark:bg-slate-800 dark:text-slate-100"
                />
                <button
                  onClick={() => saveName.mutate(nameDraft)}
                  className="rounded-md p-1.5 text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/30"
                  aria-label="Guardar nombre"
                >
                  <Check className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setNameDraft(data.name ?? '');
                  setEditingName(true);
                }}
                className="group inline-flex items-center gap-1.5 text-lg font-semibold text-slate-900 dark:text-slate-100"
                title="Editar nombre"
              >
                {displayName}
                <Pencil className="h-3.5 w-3.5 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            )}
            {showPushName && (
              <p className="mt-0.5 text-xs text-slate-400">Se muestra en WhatsApp como “{data.pushName}”</p>
            )}
            {manual.name && <ManualChip at={manual.name} />}
          </div>
        </div>

        {!data.isGroup && (
          <BotonPersonal
            personal={data.leadCard?.personal === true}
            loading={togglePersonal.isPending}
            onToggle={(p) => togglePersonal.mutate(p)}
          />
        )}

        {/* Estado del lead (temperatura) + acciones rápidas — requieren conversación */}
        {conversationId && (
          <>
            {/* Control manual del bot: pausar (atención humana) ↔ reactivar. */}
            <BotControlToggle
              conversationId={conversationId}
              contactId={contactId}
              status={conversationStatus}
            />
            {conversacion?.leadMotivo && (
              <div className="rounded-lg bg-brand-50 px-3 py-2 text-sm dark:bg-slate-800">
                <div className="text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">Por qué</div>
                <p>{conversacion.leadMotivo}</p>
              </div>
            )}
            <LeadStatusSection
              conversationId={conversationId}
              contactId={contactId as string}
              current={leadStatus ?? data.leadStatus}
            />
            <section className="space-y-1.5">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Historial</p>
              {temperaturas && temperaturas.length > 0 ? (
                <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-300">
                  {temperaturas.map((t, i) => (
                    <li key={i} title={t.motivo}>
                      {LEAD_STATUS_OPTIONS.find((o) => o.value === t.a)?.label ?? t.a ?? '—'} ·{' '}
                      {dateTime(t.fecha)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400">Sin cambios de temperatura todavía.</p>
              )}
            </section>
            <QuickActionsSection
              conversationId={conversationId}
              contactId={contactId as string}
              status={conversationStatus}
            />
          </>
        )}
      </div>

      {/* Sus datos */}
      <div className={cn('space-y-5', dosColumnas && 'md:col-start-2 md:row-span-2 md:row-start-1')}>
        {/* Datos detectados — editables a mano por el humano */}
        <EditableLeadFieldsSection
          key={`edit-${contactId}`}
          contactId={contactId as string}
          card={data.leadCard}
          cerrado={(leadStatus ?? data.leadStatus) === 'CERRADO'}
        />

        {/* Tarjeta del lead (solo lectura): el resto de lo que detectó el agente */}
        <LeadCardSection details={data} />
      </div>

      {/* Lo demás */}
      <div className={cn('space-y-5', dosColumnas && 'md:col-start-1')}>
        {/* Etiquetas (chips) */}
        <TagsSection contactId={contactId as string} />

        {/* Notas internas (autoguardado) */}
        <InternalNotesSection
          key={`notes-${contactId}`}
          contactId={contactId as string}
          initial={readNote(data.leadCard)}
        />

        {/* Número de teléfono (corregible a mano; el ruteo va por el JID) */}
        <section className="space-y-1.5">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
            <Phone className="h-3.5 w-3.5" /> Número de teléfono
          </p>
          {editingPhone ? (
            <div className="flex items-center gap-1.5">
              <input
                autoFocus
                value={phoneDraft}
                placeholder="18095550101"
                onChange={(e) => setPhoneDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitPhone();
                  if (e.key === 'Escape') setEditingPhone(false);
                }}
                className="h-9 w-full rounded-md border border-brand-400 bg-white px-2 text-sm focus:outline-none dark:bg-slate-800 dark:text-slate-100"
              />
              <button
                onClick={commitPhone}
                className="rounded-md p-1.5 text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/30"
                aria-label="Guardar teléfono"
              >
                <Check className="h-4 w-4" />
              </button>
            </div>
          ) : data.phone ? (
            <p className="flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400">
              <Check className="h-4 w-4" /> {formatPhoneNumber(data.phone)}
              <button
                onClick={() => {
                  setPhoneDraft(data.phone ?? '');
                  setEditingPhone(true);
                }}
                className="text-slate-400 hover:text-brand-600"
                aria-label="Corregir teléfono"
                title="Corregir teléfono"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              {manual.phone && <ManualChip at={manual.phone} />}
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-amber-600 dark:text-amber-400">⚠️ No disponible aún</p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void retryResolve()}
                  loading={isFetching}
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Reintentar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPhoneDraft('');
                    setEditingPhone(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" /> Cargar a mano
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* ID interno */}
        {secciones.idInterno && (
          <section className="space-y-1">
            <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
              <KeyRound className="h-3.5 w-3.5" /> ID interno
            </p>
            <p className="break-all font-mono text-xs text-slate-400">{data.externalId}</p>
          </section>
        )}

        {/* Estadísticas */}
        <section className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">📊 Estadísticas</p>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <Stat icon={<MessagesSquare className="h-4 w-4" />} label="Mensajes" value={String(data.messageCount)} />
            <Stat icon={<FolderOpen className="h-4 w-4" />} label="Conversaciones" value={String(data.conversationCount)} />
            {secciones.costoApi && (
              <Stat
                icon={<DollarSign className="h-4 w-4" />}
                label="Costo API est."
                value={`$${data.estimatedApiCost.toFixed(4)}`}
              />
            )}
            <Stat
              icon={<CalendarClock className="h-4 w-4" />}
              label="Primera interacción"
              value={relativeTime(data.createdAt)}
            />
          </dl>
          {secciones.costoApi && (
            <p className="text-[11px] text-slate-400">
              💰 Costo estimado (no exacto): se calcula por cantidad de mensajes.
            </p>
          )}
        </section>

        {/* Canal y tipo */}
        <section className="space-y-2">
          <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
            <Hash className="h-4 w-4 text-slate-400" />
            Canal: {data.channelName ?? data.channelId}
            {data.channelType && <Badge variant="info">{data.channelType}</Badge>}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
            <Users className="h-4 w-4 text-slate-400" />
            Tipo: {data.isGroup ? `Grupo${data.memberCount ? ` (${data.memberCount} miembros)` : ''}` : 'Individual'}
          </div>
        </section>
      </div>
    </div>
  );
}

/** Lee un string no vacío de la tarjeta del lead (profileDataJson). */
function leadStr(card: Record<string, unknown>, key: string): string | null {
  const v = card[key];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/**
 * Sección "Tarjeta del lead" (solo lectura): temperatura + campos que el agente
 * extrajo. Oculta los campos vacíos; si no hay ningún dato, muestra un placeholder.
 */
function LeadCardSection({ details }: { details: ContactDetails }) {
  const card = details.leadCard ?? {};
  const crmView = useCrmView();

  const precioNum = typeof card.precio_modelo === 'number' ? (card.precio_modelo as number) : null;
  const moneda = crmView?.currencyPrefix ?? 'RD$';
  const precio = precioNum ? `${moneda}${precioNum.toLocaleString('en-US')}` : null;
  const comprobante = leadStr(card, 'comprobante_fiscal');
  const rnc = leadStr(card, 'rnc');
  const comprobanteText =
    comprobante && rnc ? `${comprobante} · RNC ${rnc}` : comprobante ?? (rnc ? `RNC ${rnc}` : null);

  // Material/Zona/Modelo se muestran arriba en el bloque EDITABLE; acá quedan
  // los campos que sólo extrae el agente, para no duplicar la información.
  const rows: { label: string; value: string | null }[] = [
    { label: 'Precio', value: precio },
    { label: 'Timing', value: leadStr(card, 'timing') },
    { label: 'Comprobante fiscal', value: comprobanteText },
    { label: 'Objeciones', value: leadStr(card, 'objeciones') },
    { label: 'Resumen de cierre', value: leadStr(card, 'resumen_cierre') },
    { label: 'Fuente', value: leadStr(card, 'fuente') },
  ];
  const filled = rows.filter((r) => r.value);

  return (
    <section className="space-y-2">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
        <ClipboardList className="h-3.5 w-3.5" /> Detectado por el agente
      </p>
      {filled.length === 0 ? (
        <p className="text-sm text-slate-400">— Sin datos del agente todavía.</p>
      ) : (
        <dl className="space-y-1 text-sm">
          {filled.map((r) => (
            <div key={r.label} className="flex gap-2">
              <dt className="w-32 shrink-0 text-slate-500 dark:text-slate-400">{r.label}</dt>
              <dd className="min-w-0 flex-1 break-words font-medium text-slate-800 dark:text-slate-200">
                {r.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/** Lee la nota interna (single string) guardada en profileDataJson. */
function readNote(card: Record<string, unknown>): string {
  const v = card?.[INTERNAL_NOTES_KEY];
  return typeof v === 'string' ? v : '';
}

/**
 * Temperatura comercial del lead (editable). Persiste en la CONVERSACIÓN abierta
 * vía `PATCH /api/conversations/:id/lead-status` (endpoint existente). Al guardar
 * refresca la conversación, la bandeja y la ficha.
 */
function LeadStatusSection({
  conversationId,
  contactId,
  current,
}: {
  conversationId: string;
  contactId: string;
  current: LeadStatus | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const crmView = useCrmView();
  const save = useMutation({
    mutationFn: (value: LeadStatus | null) =>
      conversationsApi.updateLeadStatus(conversationId, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['contact', contactId] });
      void qc.invalidateQueries({ queryKey: ['cartera'] });
      toast.success('Estado del lead actualizado');
    },
    onError: () => toast.error('No se pudo actualizar el estado del lead'),
  });

  return (
    <section className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
        <Thermometer className="h-3.5 w-3.5" /> Estado del lead
      </p>
      <Select
        value={current ?? ''}
        disabled={save.isPending}
        onChange={(e) => {
          const valor = (e.target.value || null) as LeadStatus | null;
          // Cerrar crea los borradores de postventa: se pregunta antes, como en la tabla y el kanban.
          const pregunta = confirmarTemperatura(valor);
          if (pregunta && !window.confirm(pregunta)) return;
          save.mutate(valor);
        }}
      >
        <option value="">Sin clasificar</option>
        {leadStatusOptionsFrom(crmView?.crm).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </section>
  );
}

/**
 * Acciones rápidas coherentes con los enums existentes (ver `quickActionTransition`):
 * Agendar → crea un trabajo (capa de operaciones, PASO 2) vía modal,
 * Escalar → WAITING_HUMAN, Descartar → NO_CERRADO + CLOSED.
 */
function QuickActionsSection({
  conversationId,
  contactId,
  status,
}: {
  conversationId: string;
  contactId: string;
  status?: ConversationStatus;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const me = useAuthStore((s) => s.user);
  const [agendarOpen, setAgendarOpen] = useState(false);
  const conAgendar = seccionesVisibles(useCrmView()?.crm).agendar;

  const run = useMutation({
    mutationFn: async (action: QuickAction) => {
      const t = quickActionTransition(action);
      if (t.assignToMe) await conversationsApi.assignUser(conversationId, me?.id ?? null);
      if (t.leadStatus) await conversationsApi.updateLeadStatus(conversationId, t.leadStatus);
      if (t.status) await conversationsApi.updateStatus(conversationId, t.status);
      return t;
    },
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['contact', contactId] });
      void qc.invalidateQueries({ queryKey: ['cartera'] });
      toast.success(t.toast);
    },
    onError: () => toast.error('No se pudo completar la acción'),
  });

  const isClosed = status === 'CLOSED' || status === 'ARCHIVED';

  return (
    <section className="space-y-1.5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Acciones rápidas</p>
      <div className={`grid gap-1.5 ${conAgendar ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {conAgendar && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAgendarOpen(true)}
            title="Agendar un trabajo para este cliente"
          >
            <CalendarPlus className="h-3.5 w-3.5" /> Agendar
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => run.mutate('escalate')}
          loading={run.isPending && run.variables === 'escalate'}
          disabled={status === 'WAITING_HUMAN'}
          title="Pasar la conversación a un humano"
        >
          <ArrowUpRight className="h-3.5 w-3.5" /> Escalar
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => run.mutate('discard')}
          loading={run.isPending && run.variables === 'discard'}
          disabled={isClosed}
          title="Descartar el lead y cerrar la conversación"
        >
          <Trash2 className="h-3.5 w-3.5" /> Descartar
        </Button>
      </div>
      {conAgendar && (
        <JobFormModal
          open={agendarOpen}
          onClose={() => setAgendarOpen(false)}
          contactId={contactId}
          conversationId={conversationId}
        />
      )}
    </section>
  );
}

/** Chip "editado a mano" con la fecha en el title (sella el server al editar). */
function ManualChip({ at }: { at: string }) {
  const fecha = new Date(at);
  const title = Number.isNaN(fecha.getTime())
    ? 'Editado a mano'
    : `Editado a mano el ${fecha.toLocaleString('es-DO', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`;
  return (
    <span
      title={title}
      className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400"
    >
      a mano
    </span>
  );
}

/**
 * Datos detectados EDITABLES por el humano, cada uno con el editor de su tipo (tanda 6:
 * desplegable, número o texto; antes todo era texto libre). Guarda solo si cambió, con
 * merge no destructivo vía `PATCH /api/contacts/:id` { profileData }.
 *
 * La etapa es la excepción: va por `cambiarEtapa` (la misma ruta que la tabla y el kanban de
 * «Clientes»), que es la que cierra la cita al pasar a «Zoom hecho» y crea el «caerle arriba»
 * del Tour. Con el PATCH genérico esos efectos se saltaban. Vaciarla sí va por el PATCH.
 */
function EditableLeadFieldsSection({
  contactId,
  card,
  cerrado,
}: {
  contactId: string;
  card: Record<string, unknown>;
  /** Temperatura CERRADO: su etapa ya no se mueve desde aquí. */
  cerrado: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const crmView = useCrmView();
  const manual = manualEditsFrom(card);

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['contact', contactId] });
    // La etapa y lo que se sabe de la pareja se ven en la tarjeta de «Clientes».
    void qc.invalidateQueries({ queryKey: ['cartera'] });
  };
  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      contactsApi.updateContact(contactId, { profileData: patch }),
    onSuccess: () => {
      refrescar();
      toast.success('Dato actualizado');
    },
    onError: () => toast.error('No se pudo guardar el dato'),
  });
  const moverDeEtapa = useMutation({
    mutationFn: (etapa: string) => cambiarEtapa(contactId, etapa),
    onSuccess: () => {
      refrescar();
      toast.success('Etapa actualizada');
    },
    // El servidor dice por qué («Este cliente ya está cerrado…»).
    onError: (e) => toast.error('No se pudo cambiar la etapa', e instanceof Error ? e.message : undefined),
  });

  return (
    <section className="space-y-2">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
        <Pencil className="h-3.5 w-3.5" /> Datos del lead (editable)
      </p>
      <div className="space-y-2">
        {editableLeadFieldsFrom(crmView?.crm).map((f) => (
          <CampoDeLaFicha
            key={f.key}
            campo={f}
            valor={valorDeFicha(card, f.key)}
            aMano={manual[f.key]}
            bloqueado={f.key === 'etapa' && cerrado}
            onGuardar={(valor) => (f.key === 'etapa' && valor ? moverDeEtapa.mutateAsync(valor) : save.mutateAsync({ [f.key]: valor }))}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * Un campo de «Datos del lead»: su nombre, el editor de su tipo y, si no se puede tocar (la
 * etapa de un cliente ya cerrado), la línea que dice cómo. Sin estado: se prueba con
 * renderToStaticMarkup.
 */
export function CampoDeLaFicha({
  campo,
  valor,
  aMano,
  bloqueado = false,
  onGuardar,
}: {
  campo: EditableLeadField;
  valor: string;
  /** Cuándo se editó a mano (ISO), si se editó. */
  aMano?: string;
  bloqueado?: boolean;
  onGuardar: (valor: string) => void | Promise<unknown>;
}) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm">
        <span className="flex w-28 shrink-0 flex-wrap items-center gap-x-1 gap-y-0.5 text-slate-500 dark:text-slate-400">
          {campo.label}
          {aMano && <ManualChip at={aMano} />}
        </span>
        <EditorDeCampo campo={campo} valor={valor} deshabilitado={bloqueado} onGuardar={onGuardar} />
      </label>
      {bloqueado && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Ya está cerrado: su etapa no se mueve. Para reabrirlo, cámbiale el «Estado del lead».
        </p>
      )}
    </div>
  );
}

/**
 * Etiquetas del contacto (chips). Reusa el sistema de tags existente: lista las
 * etiquetas de tipo CONTACT (`GET /api/tags`), muestra las asignadas (de
 * `GET /api/contacts/:id`) y añade/quita con `POST/DELETE /api/contacts/:id/tags`.
 */
function TagsSection({ contactId }: { contactId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);

  const contact = useQuery({
    queryKey: ['contact', contactId],
    queryFn: () => contactsApi.getContact(contactId),
  });
  const allTags = useQuery({
    queryKey: ['tags', 'CONTACT'],
    queryFn: () => tagsApi.listTags('CONTACT'),
  });

  const assigned = contact.data?.tags?.map((t) => t.tag) ?? [];
  const assignedIds = new Set(assigned.map((t) => t.id));
  const available = (allTags.data ?? []).filter((t) => !assignedIds.has(t.id));

  const invalidate = () => qc.invalidateQueries({ queryKey: ['contact', contactId] });

  const add = useMutation({
    mutationFn: (tagId: string) => contactsApi.addTag(contactId, tagId),
    onSuccess: () => {
      setAdding(false);
      void invalidate();
    },
    onError: () => toast.error('No se pudo añadir la etiqueta'),
  });
  const remove = useMutation({
    mutationFn: (tagId: string) => contactsApi.removeTag(contactId, tagId),
    onSuccess: () => void invalidate(),
    onError: () => toast.error('No se pudo quitar la etiqueta'),
  });

  return (
    <section className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
        <TagIcon className="h-3.5 w-3.5" /> Etiquetas
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        {assigned.map((t) => (
          <span
            key={t.id}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-white"
            style={{ backgroundColor: t.color }}
          >
            {t.name}
            <button
              onClick={() => remove.mutate(t.id)}
              className="rounded-full hover:bg-black/20"
              aria-label={`Quitar ${t.name}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {assigned.length === 0 && <span className="text-sm text-slate-400">— Sin etiquetas.</span>}
      </div>

      {adding ? (
        <Select
          autoFocus
          defaultValue=""
          onChange={(e) => e.target.value && add.mutate(e.target.value)}
          onBlur={() => setAdding(false)}
        >
          <option value="" disabled>
            Elige una etiqueta…
          </option>
          {available.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
      ) : (
        available.length > 0 && (
          <button
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            <Plus className="h-3.5 w-3.5" /> Añadir etiqueta
          </button>
        )
      )}
    </section>
  );
}

/**
 * Notas internas del lead: textarea con AUTOGUARDADO (debounce ~2s). Se persiste
 * como un único string en `profileDataJson.notas_internas` vía
 * `PATCH /api/contacts/:id` { profileData } (merge no destructivo). Hace flush del
 * guardado pendiente al desmontar para no perder lo último escrito.
 */
function InternalNotesSection({ contactId, initial }: { contactId: string; initial: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState(initial);
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');

  const save = useMutation({
    mutationFn: (notas: string) =>
      contactsApi.updateContact(contactId, { profileData: { [INTERNAL_NOTES_KEY]: notas } }),
    onSuccess: () => {
      setState('saved');
      void qc.invalidateQueries({ queryKey: ['contact', contactId] });
    },
    onError: () => {
      setState('idle');
      toast.error('No se pudieron guardar las notas');
    },
  });

  // El guardador real cambia de identidad en cada render (cierra sobre `save`);
  // lo guardamos en un ref para que el debounce (estable) siempre llame al actual.
  const saveRef = useRef(save.mutate);
  saveRef.current = save.mutate;
  const debouncedSave = useRef(
    debounce((notas: string) => {
      setState('saving');
      saveRef.current(notas);
    }, 2000),
  ).current;

  // Flush del guardado pendiente al desmontar (cerrar panel / cambiar de contacto).
  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

  return (
    <section className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
          <StickyNote className="h-3.5 w-3.5" /> Notas internas
        </p>
        {state === 'saving' ? (
          <span className="flex items-center gap-1 text-[11px] text-slate-400">
            <Loader2 className="h-3 w-3 animate-spin" /> Guardando…
          </span>
        ) : state === 'saved' ? (
          <span className="flex items-center gap-1 text-[11px] text-emerald-500">
            <Check className="h-3 w-3" /> Guardado
          </span>
        ) : null}
      </div>
      <Textarea
        rows={4}
        value={text}
        placeholder="Notas privadas del equipo (no las ve el cliente)…"
        onChange={(e) => {
          setText(e.target.value);
          setState('idle');
          debouncedSave(e.target.value);
        }}
        onBlur={() => debouncedSave.flush()}
      />
    </section>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-md bg-surface-muted p-2 dark:bg-slate-800/50">
      <dt className="flex items-center gap-1 text-[11px] text-slate-400">
        {icon} {label}
      </dt>
      <dd className="mt-0.5 truncate font-medium text-slate-800 dark:text-slate-200">{value}</dd>
    </div>
  );
}

/**
 * «Es personal» ↔ «Quitar de personales» (tanda 5, Ola E). Sin estado: se prueba
 * con renderToStaticMarkup.
 */
export function BotonPersonal({
  personal,
  loading = false,
  onToggle,
}: {
  personal: boolean;
  loading?: boolean;
  onToggle: (personal: boolean) => void;
}) {
  return (
    <section className="space-y-1">
      <Button variant="outline" size="sm" className="w-full" onClick={() => onToggle(!personal)} loading={loading}>
        {personal ? (
          <>
            <UserCheck className="h-4 w-4" /> Quitar de personales
          </>
        ) : (
          <>
            <UserX className="h-4 w-4" /> Es personal: que el agente no le hable
          </>
        )}
      </Button>
      {personal && (
        <p className="text-center text-xs text-slate-500 dark:text-slate-400">
          Personal: el agente no le escribe ni le prepara seguimientos.
        </p>
      )}
    </section>
  );
}

function ContactBlockButton({
  blocked,
  onToggle,
  loading,
}: {
  blocked: boolean;
  onToggle: (blocked: boolean) => void;
  loading: boolean;
}) {
  return (
    <Button variant="outline" size="sm" onClick={() => onToggle(!blocked)} loading={loading}>
      {blocked ? (
        <>
          <ShieldCheck className="h-4 w-4" /> Desbloquear
        </>
      ) : (
        <>
          <Ban className="h-4 w-4" /> Bloquear
        </>
      )}
    </Button>
  );
}
